/**
 * You — the user's profile + track record.
 *
 * Composition:
 *   • Profile header (avatar + username + member-since)
 *   • Track-record stat tiles: total picks, win rate, lifetime units
 *   • Sharp8 engine benchmark · the lifetime engine record so the
 *     user has a real anchor to compare their own performance to
 *     (brief §1.4 · the public-ledger credibility wedge)
 *   • Picks list — chronological, with status badges + result
 *   • Empty state when the user hasn't placed any picks yet
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { User, UserPick } from '@sharp8/types';
import { tg } from '../lib/telegram';
import { subscribe } from '../lib/eventStream';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface Props {
  me: { user: User; guest: boolean } | null;
  /** Lets the You view push bankroll updates back to App-level me
   *  state whenever the picks SSE event carries a new value (e.g.
   *  after the cron credits a settled-pick payout). Without this
   *  the BalanceHero stays stuck on the bankroll loaded at boot. */
  onBankrollChange?(newBankroll: number): void;
  /** Tapping an Activity row navigates to the match detail. FT
   *  matches land on the SettledRecapCard (post-mortem); in-play /
   *  pre-match land on the edge card with the AlreadyPlaced status
   *  block — so the user can revisit the call either way. */
  onOpenMatch?(matchId: string): void;
}

interface PicksState {
  picks: UserPick[] | null;
  error: string | null;
}

interface Sharp8Window {
  settled: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number | null;
  units1u: number;
}

async function fetchPicks(): Promise<UserPick[]> {
  const initData = tg()?.initData ?? '';
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/me/picks`, {
    headers: initData ? { 'X-Telegram-Init-Data': initData } : {},
  });
  // 401 = unauthenticated (no Telegram or stale initData). Treat as
  // "no picks yet" instead of a hard error — the empty-state UI is the
  // right experience for both first-load and unauthenticated visits.
  if (res.status === 401) return [];
  if (!res.ok) throw new Error(`/me/picks ${res.status}`);
  const j = await res.json() as { picks: UserPick[] };
  return j.picks;
}

async function fetchSharp8Lifetime(): Promise<Sharp8Window | null> {
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/stats/sharp8`);
  if (!res.ok) return null;
  const j = await res.json() as { all: Sharp8Window };
  return j.all ?? null;
}

function calcStats(picks: UserPick[]) {
  let won = 0, lost = 0, pending = 0, pushed = 0, voided = 0;
  // Profit-in-$ on settled picks — same metric as the Compete
  // leaderboard's profitDollars (server-side). Matches the F2P game
  // model (everyone starts at $1,000) instead of the older per-stake
  // unit normalization.
  let lifetimeProfit = 0;
  let totalStake = 0;

  for (const p of picks) {
    totalStake += p.stake;
    if (p.status === 'won') {
      won++;
      if (p.payout !== null) lifetimeProfit += (p.payout - p.stake);
    } else if (p.status === 'lost') {
      lost++;
      // Half-line losses get half the stake back as a refund, so the
      // realized loss is stake − payout, not the full stake.
      const loss = p.stake - (p.payout ?? 0);
      lifetimeProfit -= loss;
    } else if (p.status === 'pushed') {
      pushed++;
    } else if (p.status === 'voided') {
      voided++;
    } else {
      pending++;
    }
  }

  const settled = won + lost + pushed;
  const winRate = settled > 0 ? won / (won + lost) : null;

  return { total: picks.length, won, lost, pending, pushed, voided, winRate, lifetimeProfit, totalStake };
}

export function You({ me, onBankrollChange, onOpenMatch }: Props) {
  const [state, setState] = useState<PicksState>({ picks: null, error: null });
  const [sharp8, setSharp8] = useState<Sharp8Window | null>(null);

  // Live updates · SSE-driven.
  //   · Initial one-shot fetch so the list renders before the first SSE
  //     event lands (server polls @ 8s, so without the seed the user
  //     could see "Loading…" for a few seconds).
  //   · EventSource subscription receives 'picks' events whenever the
  //     server detects a snapshot change. Heartbeats keep the
  //     connection alive but don't trigger UI work.
  //   · onError falls back to the one-shot fetch — if the WebView
  //     can't open EventSource at all (old Android, restrictive proxy),
  //     we still get fresh data on tab focus.
  useEffect(() => {
    let cancelled = false;
    const refetch = () => {
      fetchPicks()
        .then((picks) => { if (!cancelled) setState({ picks, error: null }); })
        .catch((e)    => { if (!cancelled) setState((s) => ({ picks: s.picks ?? [], error: String(e?.message ?? e) })); });
    };
    refetch();  // initial seed
    const handle = subscribe<{ picks: UserPick[]; bankroll?: number }>({
      path: 'streams/me/picks',
      events: ['picks'],
      withInitData: true,
      onEvent: (payload) => {
        if (cancelled) return;
        setState({ picks: payload.picks, error: null });
        // Settled-pick payouts get credited to users.bankroll on the
        // backend; this event is the moment the client should adopt
        // the new value so the BalanceHero / Home BankrollPill flip.
        if (typeof payload.bankroll === 'number') {
          onBankrollChange?.(payload.bankroll);
        }
      },
      onError: () => {
        // Soft fallback — refetch one-shot. EventSource will retry the
        // connection on its own; we just ensure the user sees fresh
        // data while it bounces.
        refetch();
      },
    });
    return () => { cancelled = true; handle.close(); };
  }, [onBankrollChange]);

  // Sharp8 lifetime engine record · benchmark for the user's record.
  // Soft-fail to null so the benchmark just hides if the endpoint
  // errors or the engine has no settled calls yet.
  useEffect(() => {
    let cancelled = false;
    fetchSharp8Lifetime()
      .then((s) => { if (!cancelled) setSharp8(s); })
      .catch(() => { /* benchmark is optional — silent failure */ });
    return () => { cancelled = true; };
  }, []);

  if (!me) {
    return (
      <div className="you-loading">Loading…</div>
    );
  }

  const isGuest = me.guest;
  const user = me.user;

  return (
    <div className="you">
      {/* PROFILE HEADER */}
      <section className="you__profile">
        {user.photoUrl ? (
          <img className="you__avatar" src={user.photoUrl} alt={user.username ?? 'You'} />
        ) : (
          <div className="you__avatar you__avatar--fallback">
            {(user.username ?? 'U')[0]?.toUpperCase() ?? 'U'}
          </div>
        )}
        <div className="you__name">
          {isGuest ? 'Guest' : (user.username ?? 'Sharp8 user')}
        </div>
        <div className="you__since">
          {isGuest
            ? 'Open from Telegram to track your edge'
            : `Member since ${new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
        </div>
      </section>

      {/* GAME BANKROLL · F2P balance starting from $1,000 (brief §4.4) */}
      {!isGuest && <BalanceHero bankroll={user.bankroll} />}

      {/* TRACK RECORD STATS */}
      <Stats picks={state.picks} loading={state.picks === null} />

      {/* SHARP8 ENGINE BENCHMARK · anchor for the user's record above.
          Renders only when the engine has at least one settled call. */}
      {sharp8 && sharp8.settled > 0 && <Sharp8Benchmark s={sharp8} />}

      {/* PICKS LIST · split into Open / Settled segments so a long
          history stays scannable. Open = riding now (pending payout,
          match not finished). Settled = the post-mortem ledger. */}
      <Activity
        picks={state.picks}
        isGuest={isGuest}
        onOpenMatch={onOpenMatch}
      />
    </div>
  );
}

/* ---------- Activity (segmented + paginated) ---------- */

type Segment = 'open' | 'settled';

function partitionPicks(picks: UserPick[]) {
  const open: UserPick[] = [];
  const settled: UserPick[] = [];
  for (const p of picks) {
    // A pick is "open" until both the match is finished AND the pick
    // has been settled. Pending picks on FT matches still show as open
    // (cron hasn't credited yet) — gives the user the right mental
    // model: "this is still being resolved".
    const matchDone = p.match?.state === 'ft' || p.match?.state === 'settled';
    const pickDone  = p.status === 'won' || p.status === 'lost' || p.status === 'pushed' || p.status === 'voided';
    if (matchDone && pickDone) settled.push(p);
    else open.push(p);
  }
  // Open: soonest kickoff first; rows without a kickoff sink to the
  // bottom. Settled: most recent placement first.
  open.sort((a, b) => {
    const ka = a.match ? new Date(a.match.kickoff).getTime() : Number.POSITIVE_INFINITY;
    const kb = b.match ? new Date(b.match.kickoff).getTime() : Number.POSITIVE_INFINITY;
    return ka - kb;
  });
  settled.sort((a, b) => new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime());
  return { open, settled };
}

const SETTLED_PAGE_SIZE = 20;

function Activity({
  picks,
  isGuest,
  onOpenMatch,
}: {
  picks: UserPick[] | null;
  isGuest: boolean;
  onOpenMatch?: (matchId: string) => void;
}) {
  const { open, settled } = useMemo(
    () => (picks ? partitionPicks(picks) : { open: [], settled: [] }),
    [picks],
  );
  // Default to whichever segment has rows; prefer Open if both do —
  // that's the "what am I riding right now" view bettors return for.
  const [seg, setSeg] = useState<Segment>('open');
  const [shown, setShown] = useState(SETTLED_PAGE_SIZE);

  // Auto-flip to Settled on the *first* successful load when Open is
  // empty but Settled has items — avoids stranding the user on an
  // empty tab. A ref keeps this one-shot so later SSE pushes can't
  // override the user's manual segment choice.
  const flipDone = useRef(false);
  useEffect(() => {
    if (flipDone.current || !picks) return;
    flipDone.current = true;
    if (open.length === 0 && settled.length > 0) setSeg('settled');
  }, [picks, open.length, settled.length]);

  if (picks === null) {
    return (
      <section className="you__section">
        <div className="you__section-head">Activity</div>
        <div className="you__loading">Loading your activity…</div>
      </section>
    );
  }
  if (picks.length === 0) {
    return (
      <section className="you__section">
        <div className="you__section-head">Activity</div>
        <EmptyState isGuest={isGuest} />
      </section>
    );
  }

  const settledVisible = settled.slice(0, shown);
  const hasMore = settled.length > shown;

  return (
    <section className="you__section">
      <div className="you__section-head">Activity</div>

      <div className="you__seg" role="tablist" aria-label="Activity filter">
        <button
          type="button"
          role="tab"
          aria-selected={seg === 'open'}
          className={`you__seg-btn${seg === 'open' ? ' you__seg-btn--active' : ''}`}
          onClick={() => setSeg('open')}
        >
          <span className="you__seg-label">Open</span>
          {open.length > 0 && (
            <span className="you__seg-count">{open.length}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={seg === 'settled'}
          className={`you__seg-btn${seg === 'settled' ? ' you__seg-btn--active' : ''}`}
          onClick={() => setSeg('settled')}
        >
          <span className="you__seg-label">Settled</span>
          {settled.length > 0 && (
            <span className="you__seg-count">{settled.length}</span>
          )}
        </button>
      </div>

      {seg === 'open' ? (
        open.length === 0 ? (
          <SegmentEmpty
            title="No open picks"
            body="When you place a bet on an upcoming or live match, it lands here."
          />
        ) : (
          <ul className="you__picks">
            {open.map((p) => (
              <PickRow key={p.id} pick={p} onOpenMatch={onOpenMatch} />
            ))}
          </ul>
        )
      ) : settled.length === 0 ? (
        <SegmentEmpty
          title="No settled picks yet"
          body="Your post-mortem ledger appears here once matches you've bet on finish."
        />
      ) : (
        <>
          <GroupedSettled picks={settledVisible} onOpenMatch={onOpenMatch} />
          {hasMore && (
            <button
              type="button"
              className="you__more"
              onClick={() => setShown((n) => n + SETTLED_PAGE_SIZE)}
            >
              Show {Math.min(SETTLED_PAGE_SIZE, settled.length - shown)} more
              <span className="you__more-of"> · {shown} of {settled.length}</span>
            </button>
          )}
        </>
      )}
    </section>
  );
}

function SegmentEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="you__seg-empty">
      <div className="you__seg-empty-title">{title}</div>
      <div className="you__seg-empty-body">{body}</div>
    </div>
  );
}

/* ---------- Settled grouping by date ---------- */

function startOfLocalDay(d: Date): number {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return x.getTime();
}

/** Bucket label for a settled pick. Buckets: TODAY, YESTERDAY, then
 *  per-date going back ("MAY 20", "DEC 12 · 2025" if year crosses).
 *  Per-date is the right grain for betting — each matchday is its own
 *  "session" the user mentally indexes by. */
function dateBucketLabel(placedAt: string, todayMs: number, thisYear: number): string {
  const d = new Date(placedAt);
  const dayMs = startOfLocalDay(d);
  const dayDiff = Math.round((todayMs - dayMs) / 86_400_000);
  if (dayDiff <= 0) return 'TODAY';
  if (dayDiff === 1) return 'YESTERDAY';
  const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = d.getDate();
  if (d.getFullYear() === thisYear) return `${month} ${day}`;
  return `${month} ${day} · ${d.getFullYear()}`;
}

function GroupedSettled({
  picks,
  onOpenMatch,
}: {
  picks: UserPick[];
  onOpenMatch?: (matchId: string) => void;
}) {
  const now = new Date();
  const todayMs = startOfLocalDay(now);
  const thisYear = now.getFullYear();

  // Preserve incoming order (already desc by placedAt) while bucketing.
  const groups: { label: string; rows: UserPick[] }[] = [];
  let current: { label: string; rows: UserPick[] } | null = null;
  for (const p of picks) {
    const label = dateBucketLabel(p.placedAt, todayMs, thisYear);
    if (!current || current.label !== label) {
      current = { label, rows: [] };
      groups.push(current);
    }
    current.rows.push(p);
  }

  return (
    <div className="you__groups">
      {groups.map((g) => (
        <div className="you__group" key={g.label}>
          <div className="you__day">{g.label}</div>
          <ul className="you__picks">
            {g.rows.map((p) => (
              <PickRow key={p.id} pick={p} onOpenMatch={onOpenMatch} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Stats({ picks, loading }: { picks: UserPick[] | null; loading: boolean }) {
  if (loading || !picks) {
    return (
      <section className="you__stats you__stats--loading">
        <div className="you__stat-tile">—</div>
        <div className="you__stat-tile">—</div>
        <div className="you__stat-tile">—</div>
      </section>
    );
  }
  const s = calcStats(picks);

  const winRatePct = s.winRate !== null ? `${(s.winRate * 100).toFixed(0)}%` : '—';
  const profitPrefix = s.lifetimeProfit >= 0 ? '+' : '−';
  const profitClass  = s.lifetimeProfit >= 0 ? 'green' : 'red';

  return (
    <section className="you__stats">
      <div className="you__stat-tile">
        <div className="you__stat-v">{s.total}</div>
        <div className="you__stat-k">Picks</div>
        {s.pending > 0 && <div className="you__stat-sub">{s.pending} pending</div>}
      </div>
      <div className="you__stat-tile">
        <div className="you__stat-v">{winRatePct}</div>
        <div className="you__stat-k">Win rate</div>
        {s.won + s.lost > 0 && (
          <div className="you__stat-sub">{s.won}-{s.lost}</div>
        )}
      </div>
      <div className="you__stat-tile">
        <div className={`you__stat-v ${profitClass}`}>
          {profitPrefix}${Math.abs(s.lifetimeProfit).toFixed(2)}
        </div>
        <div className="you__stat-k">Profit</div>
      </div>
    </section>
  );
}

function BalanceHero({ bankroll }: { bankroll: number }) {
  const diff = bankroll - 1000;
  const diffSign = diff >= 0 ? '+' : '−';
  const diffAbs = Math.abs(diff);
  const diffCls = diff >= 0 ? 'green' : 'red';
  return (
    <section className="you__balance" aria-label="Game bankroll">
      <div className="you__balance-head">
        <span className="you__balance-tag">GAME BANKROLL</span>
        <span className="you__balance-start">$1,000 start</span>
      </div>
      <div className="you__balance-amount">
        ${bankroll.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      <div className={`you__balance-delta ${diffCls}`}>
        {diff === 0 ? 'No moves yet' : `${diffSign}$${diffAbs.toFixed(2)}`}
      </div>
    </section>
  );
}

function Sharp8Benchmark({ s }: { s: Sharp8Window }) {
  const winRatePct = s.winRate !== null ? `${(s.winRate * 100).toFixed(0)}%` : '—';
  const unitsPrefix = s.units1u >= 0 ? '+' : '';
  const unitsClass  = s.units1u >= 0 ? 'green' : 'red';
  return (
    <section className="you__benchmark" aria-label="Sharp8 engine lifetime record">
      <span className="you__benchmark-label">Sharp8 lifetime</span>
      <span className="you__benchmark-divider" aria-hidden="true">·</span>
      <span className="you__benchmark-rate">{winRatePct} W</span>
      <span className="you__benchmark-divider" aria-hidden="true">·</span>
      <span className={`you__benchmark-units ${unitsClass}`}>
        {unitsPrefix}{s.units1u.toFixed(2)}u
      </span>
    </section>
  );
}

function PickRow({ pick, onOpenMatch }: { pick: UserPick; onOpenMatch?: (matchId: string) => void }) {
  const placed = new Date(pick.placedAt);
  const placedLabel = placed.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  let badge: { label: string; cls: string };
  let payoutLine: string | null = null;

  // Detect quarter-line "half" outcomes from the stored payout:
  //   half-WIN  · payout < full win (stake*price) but > stake
  //   half-LOSS · status=lost yet payout > 0 (the pushed half got refunded)
  // The settle code stores the actual dollar payout, so we recover the
  // outcome shape from data — no schema change needed.
  const fullWinPayout = pick.stake * pick.price;
  const isHalfWin  = pick.status === 'won'  && pick.payout !== null && pick.payout < fullWinPayout - 0.01;
  const isHalfLoss = pick.status === 'lost' && pick.payout !== null && pick.payout > 0.01;

  switch (pick.status) {
    case 'won':
      badge = { label: isHalfWin ? '½ WON' : 'WON', cls: 'won' };
      if (pick.payout !== null) {
        const profit = pick.payout - pick.stake;
        payoutLine = `${profit >= 0 ? '+' : '−'}$${Math.abs(profit).toFixed(2)}`;
      }
      break;
    case 'lost': {
      badge = { label: isHalfLoss ? '½ LOST' : 'LOST', cls: 'lost' };
      // Loss amount = stake − refund (full loss when payout=0; half-loss
      // when payout=stake/2 returned).
      const loss = pick.stake - (pick.payout ?? 0);
      payoutLine = `−$${loss.toFixed(2)}`;
      break;
    }
    case 'pushed':
      badge = { label: 'PUSH', cls: 'pushed' };
      payoutLine = '$0.00';
      break;
    case 'voided':
      badge = { label: 'VOID', cls: 'pushed' };
      break;
    default:
      badge = { label: 'PENDING', cls: 'pending' };
  }

  // Match summary — included by /me/picks via the JOIN.
  const m = pick.match;
  const teamsLine = m ? `${m.home.shortName} vs ${m.away.shortName}` : null;
  const leagueLine = m ? m.league.shortName : null;

  // Compute pre-match countdown for the "STARTS IN X" chip.
  // < 0 (kicked off but still in 'pre' state on our side) → "STARTING…"
  // 0-30 min            → soon-chip "STARTS IN 12M"
  // > 30 min            → just a kickoff time string ("Kicks off 19:30 UTC")
  let preChip: ReactNode = null;
  let kickoffLine: string | null = null;
  if (m?.state === 'pre') {
    const msToKickoff = new Date(m.kickoff).getTime() - Date.now();
    const minsToKickoff = Math.round(msToKickoff / 60_000);
    if (minsToKickoff <= 0) {
      preChip = <span className="you__pick-state you__pick-state--soon">STARTING…</span>;
    } else if (minsToKickoff <= 30) {
      const label = minsToKickoff >= 1
        ? `STARTS IN ${minsToKickoff}M`
        : 'STARTING…';
      preChip = <span className="you__pick-state you__pick-state--soon">{label}</span>;
    } else {
      const d = new Date(m.kickoff);
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      kickoffLine = `Kicks off ${hh}:${mm} UTC`;
    }
  }

  const stateChip =
    m?.state === 'live'    ? <span className="you__pick-state you__pick-state--live">LIVE{m.score?.minute ? ` · ${m.score.minute}` : ''}</span> :
    m?.state === 'ft'      ? <span className="you__pick-state you__pick-state--ft">FT</span> :
    m?.state === 'settled' ? <span className="you__pick-state you__pick-state--ft">SETTLED</span> :
    preChip;

  // Whole row is a tap-target so the user can pull up the match
  // detail — post-mortem for finished matches, edge + AlreadyPlaced
  // status for pre/live matches. Falls back to a non-interactive li
  // for guest / offline view (no onOpenMatch prop).
  const isClickable = !!onOpenMatch;
  const open = () => onOpenMatch?.(pick.matchId);
  const onKey = (e: KeyboardEvent<HTMLLIElement>) => {
    if (!isClickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };
  const ctaText = (m?.state === 'ft' || m?.state === 'settled')
    ? 'View post-mortem →'
    : 'View edge →';

  return (
    <li
      className={`you__pick${isClickable ? ' you__pick--clickable' : ''}`}
      onClick={isClickable ? open : undefined}
      onKeyDown={isClickable ? onKey : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `Open ${teamsLine ?? pick.line}` : undefined}
    >
      {m && (
        <div className="you__pick-match">
          <span className="you__pick-crest">
            <img src={m.home.crestUrl} alt={m.home.shortName} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
          </span>
          <div className="you__pick-teams">
            <span className="you__pick-teams-line">{teamsLine}</span>
            <span className="you__pick-league">{leagueLine}</span>
          </div>
          <span className="you__pick-crest">
            <img src={m.away.crestUrl} alt={m.away.shortName} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
          </span>
          {m.score && (
            <span className="you__pick-score">{m.score.home}–{m.score.away}</span>
          )}
          {stateChip}
        </div>
      )}
      <div className="you__pick-body">
        <div className="you__pick-main">
          <div className="you__pick-line">{pick.line}</div>
          <div className="you__pick-meta">
            ${pick.stake.toFixed(2)} @ {pick.price.toFixed(2)} · {placedLabel}
            {kickoffLine && <> · {kickoffLine}</>}
          </div>
        </div>
        <div className="you__pick-right">
          <span className={`you__pick-badge you__pick-badge--${badge.cls}`}>{badge.label}</span>
          {payoutLine && (
            <span className={`you__pick-payout you__pick-payout--${badge.cls}`}>{payoutLine}</span>
          )}
        </div>
      </div>
      {isClickable && (
        <div className="you__pick-cta" aria-hidden="true">{ctaText}</div>
      )}
    </li>
  );
}

function EmptyState({ isGuest }: { isGuest: boolean }) {
  return (
    <div className="you__empty">
      <div className="you__empty-icon">
        <svg viewBox="0 0 48 48" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <rect x="6" y="10" width="36" height="32" rx="4" />
          <path d="M14 4v8M34 4v8M6 20h36" />
        </svg>
      </div>
      <div className="you__empty-title">
        {isGuest ? 'Open in Telegram to start' : 'No picks yet'}
      </div>
      <div className="you__empty-body">
        {isGuest
          ? 'Sign in from inside Telegram and your picks will save here.'
          : 'Tap "Place bet" on any match\'s edge call. Your picks appear here.'}
      </div>
    </div>
  );
}
